import type { ElementType } from 'react';
import type { Undefinable } from '@symfa-inc/providence-types';
import type { RouteProps } from './models/route-props';
import { type RoutableTab, RouteGroup } from './route-group';

export class Route extends RouteGroup {
  #tabs: RoutableTab[] = [];
  #exact?: boolean;
  readonly #component: ElementType;
  readonly #extendPath: boolean;

  constructor({ component, extendPath = true, ...props }: RouteProps) {
    super({ ...props });
    this.#component = component;
    this.#extendPath = extendPath;
  }

  // GETTERS & SETTERS
  get exact(): Undefinable<boolean> {
    return this.#exact;
  }

  get tabs(): RoutableTab[] {
    return this.#tabs;
  }

  get extendPath(): boolean {
    return this.#extendPath;
  }

  get component(): ElementType {
    return this.#component;
  }

  // METHODS
  setExact(exact: boolean): this {
    this.#exact = exact;

    return this;
  }

  addTab(tab: RoutableTab): this {
    this.#tabs.push(tab);

    return this;
  }

  addTabs(tabs: RoutableTab[]): this {
    tabs.forEach((tab: RoutableTab) => this.addTab(tab));

    return this;
  }

  protected changeTab(tab: RoutableTab, path: string): void {
    tab.addBreadcrumbs(this.breadcrumbs);
    tab.setPath(RouteGroup.fixPath(`${path}/${tab.path}`));

    tab.tabs.forEach((childTab: RoutableTab) =>
      this.changeTab(childTab, tab.path),
    );
  }

  protected changeRoute(route: RouteGroup): void {
    const path =
      route instanceof Route && route.extendPath ? this.path : this.group?.path;

    if (path) {
      route.setPath(RouteGroup.fixPath(`${path}/${route.path}`));
    }

    route.addResolvers(this.onlyInheritanceResolvers);

    route.addBreadcrumbs(this.breadcrumbs);

    if (this.guards.length) {
      route.addGuards(this.guards, this.redirectURL!);
    }

    route.setGroup(this.group!);
  }

  findRouteByPath(path: string): Undefinable<Route> {
    const route = super.findRouteByPath(path);

    if (route && route instanceof Route) {
      return route;
    }

    const tab = this.#tabs.find((t: RoutableTab) => t.findRouteByPath(path));

    if (tab) {
      return (tab.isLink ? tab : this) as Route;
    }
  }

  formatRoutes(): this {
    super.formatRoutes();

    this.#tabs.forEach((tab: RoutableTab) => {
      this.changeRoute(tab);
      tab.formatRoutes();
    });

    return this;
  }
}
